import React, { useState, useRef, useEffect } from "react";
import { Form, Row, Col, Button } from "react-bootstrap";
import { ReactSketchCanvas } from "react-sketch-canvas";

// ── sectionsToValues ──────────────────────────────────────────────────────────
// Converts legacy sessions.sections[] to a label-keyed map for readonly display.
// Derived from normalizeNotes in EditSession.jsx.
export function sectionsToValues(sections) {
  const map = {};
  for (const n of (sections || [])) {
    const label = n.sessionsSection || "";
    if (!label) continue;
    if (n.sessionsFieldType === "multiselect") {
      map[label] = (n.sessionsSectionNotes?.selectedOptions || []).join(", ");
    } else if (n.sessionsFieldType === "image") {
      map[label] = n.sessionsImageURL || "";
    } else {
      map[label] = typeof n.sessionsSectionNotes === "string"
        ? n.sessionsSectionNotes
        : "";
    }
  }
  return map;
}

// ── sectionsToLegacyPages ─────────────────────────────────────────────────────
// Converts legacy sessions.sections[] to a synthetic pages[] structure so
// NoteRenderer (which only reads pages[]) can render pre-v16.00 sessions.
// Used with legacy={true} and readonlyValues={sectionsToValues(sections)}.
// The id for each item is set to the label string so getCapturedVal (legacy
// mode) finds values via item.id === item.label lookup.
export function sectionsToLegacyPages(sections) {
  return [{
    pageId: "legacy",
    pageTitle: "Session Notes",
    items: (sections || []).map((s, i) => ({
      id:      s.sessionsSection || String(i),
      kind:    "element",
      type:    s.sessionsFieldType === "textarea"    ? "memo"
             : s.sessionsFieldType === "multiselect" ? "checkbox"
             : s.sessionsFieldType === "image"       ? "image"
             : "text",
      label:   s.sessionsSection || "",
      colSpan: s.colSpan || 12,
    })),
  }];
}

// ── ImageAnnotationField ──────────────────────────────────────────────────────
// Fill-mode image field: shows the base image with a react-sketch-canvas overlay
// and a toolbar (colour picker, eraser toggle, undo, redo, clear).
// Defined at module level to avoid remount focus loss.
const ANNOTATION_COLORS = ["#e74c3c", "#2980b9", "#27ae60", "#f39c12", "#8e44ad", "#000000"];

function ImageAnnotationField({ item, value, onChange }) {
  const canvasRef = useRef(null);
  const [strokeColor, setStrokeColor] = useState("#e74c3c");
  const [eraserMode, setEraserMode] = useState(false);
  // Track the last value we wrote ourselves so value-watching effect can skip on own strokes
  const ownValueRef = useRef(undefined);

  // Reload canvas when value changes from outside (session switch, backfill, etc.)
  // Skip reload when the value was just set by this component's own stroke handler.
  useEffect(() => {
    if (!canvasRef.current) return;
    if (value === ownValueRef.current) return; // our own stroke — canvas already has correct state
    ownValueRef.current = value;
    canvasRef.current.clearCanvas();
    if (value) {
      try {
        const paths = JSON.parse(value);
        canvasRef.current.loadPaths(paths);
      } catch (_) {}
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync eraser mode with canvas ref
  useEffect(() => {
    canvasRef.current?.eraseMode(eraserMode);
  }, [eraserMode]);

  const handleStroke = async () => {
    if (!canvasRef.current) return;
    const paths = await canvasRef.current.exportPaths();
    const json = JSON.stringify(paths);
    ownValueRef.current = json; // mark as our own update before onChange triggers re-render
    onChange(item.id, json);
  };

  const imgUrl = item.imageUrl || item.defaultValue;

  return (
    <div style={{ textAlign: "center" }}>
      {/* Canvas over image */}
      <div style={{ position: "relative", display: "inline-block", maxWidth: "100%", userSelect: "none" }}>
        {imgUrl ? (
          <img
            src={imgUrl}
            alt={item.label || ""}
            style={{ display: "block", maxWidth: "100%", maxHeight: 400, pointerEvents: "none" }}
            draggable={false}
          />
        ) : (
          <div
            style={{
              width: 400,
              height: 300,
              background: "#f0f0f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#999",
            }}
          >
            No image
          </div>
        )}
        <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}>
          <ReactSketchCanvas
            ref={canvasRef}
            style={{ width: "100%", height: "100%", background: "transparent" }}
            canvasColor="transparent"
            strokeWidth={4}
            strokeColor={strokeColor}
            withTimestamp={false}
            allowOnlyPointerType="all"
            onStroke={handleStroke}
            exportWithBackgroundImage={false}
          />
        </div>
      </div>
      {/* Toolbar — below the image, centered */}
      <div className="d-flex align-items-center justify-content-center gap-2 mt-2 flex-wrap">
        {ANNOTATION_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => { setEraserMode(false); setStrokeColor(c); }}
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: c,
              border: "none",
              outline: strokeColor === c && !eraserMode ? "3px solid #333" : "none",
              cursor: "pointer",
              padding: 0,
            }}
          />
        ))}
        <button
          type="button"
          className={`btn btn-sm ${eraserMode ? "btn-secondary" : "btn-outline-secondary"}`}
          onClick={() => setEraserMode((e) => !e)}
          title="Eraser"
        >
          <i className="fa-light fa-eraser" />
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => canvasRef.current?.undo()}
          title="Undo"
        >
          <i className="fa-light fa-rotate-left" />
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => canvasRef.current?.redo()}
          title="Redo"
        >
          <i className="fa-light fa-rotate-right" />
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-danger"
          onClick={() => { canvasRef.current?.clearCanvas(); onChange(item.id, null); }}
          title="Clear all"
        >
          <i className="fa-light fa-trash" />
        </button>
      </div>
    </div>
  );
}

// ── ImageReadonlyField ─────────────────────────────────────────────────────────
// Readonly-mode image field: shows the base image with saved annotation paths
// rendered as a pure SVG overlay (no canvas background, no opacity issues).
// Defined at module level to avoid remount focus loss.
function ImageReadonlyField({ item, savedPaths }) {
  const imgUrl = item.imageUrl || item.defaultValue;
  const strokes = savedPaths
    ? (() => { try { return JSON.parse(savedPaths); } catch (_) { return []; } })()
    : [];

  const pathToD = (points) => {
    if (!points?.length) return "";
    return `M ${points[0].x} ${points[0].y}` +
      points.slice(1).map((p) => ` L ${p.x} ${p.y}`).join("");
  };

  const drawStrokes = strokes.filter((s) => s.drawMode !== false);
  const eraseStrokes = strokes.filter((s) => s.drawMode === false);
  const maskId = `em-${String(item.id).replace(/[^a-zA-Z0-9]/g, "_")}`;

  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ position: "relative", display: "inline-block", maxWidth: "100%", userSelect: "none" }}>
        {imgUrl ? (
          <img
            src={imgUrl}
            alt={item.label || ""}
            style={{ display: "block", maxWidth: "100%", maxHeight: 400 }}
            draggable={false}
          />
        ) : (
          <div
            style={{
              width: 400,
              height: 300,
              background: "#f0f0f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#999",
            }}
          >
            No image
          </div>
        )}
        {drawStrokes.length > 0 && (
          <svg
            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none" }}
          >
            {eraseStrokes.length > 0 && (
              <defs>
                <mask id={maskId}>
                  <rect width="100%" height="100%" fill="white" />
                  {eraseStrokes.map((s, i) => (
                    <path
                      key={i}
                      d={pathToD(s.paths)}
                      stroke="black"
                      strokeWidth={s.strokeWidth || 20}
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  ))}
                </mask>
              </defs>
            )}
            <g mask={eraseStrokes.length > 0 ? `url(#${maskId})` : undefined}>
              {drawStrokes.map((s, i) => (
                <path
                  key={i}
                  d={pathToD(s.paths)}
                  stroke={s.strokeColor || "#e74c3c"}
                  strokeWidth={s.strokeWidth || 4}
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ))}
            </g>
          </svg>
        )}
      </div>
    </div>
  );
}

// ── NoteRenderer ──────────────────────────────────────────────────────────────
/**
 * Shared renderer for all 15 field types across preview / fill / readonly modes.
 * Reads exclusively from pages[]. Never reads notes[].
 *
 * @param {Object[]}  pages            - pages[] from template document
 * @param {'preview'|'fill'|'readonly'} mode
 * @param {Object}    [values]         - fill mode: { [elementId]: value } — controlled from parent
 * @param {Function}  [onChange]       - fill mode: (elementId, value) => void
 * @param {Object}    [readonlyValues] - readonly mode: { [elementId]: value } OR { [label]: value } for legacy
 * @param {boolean}   [legacy]         - when true, readonlyValues is keyed by label not elementId
 * @param {Object}    [errors]         - optional { [elementId]: string } for fill mode validation display
 */
export default function NoteRenderer({
  pages,
  mode,
  values = {},
  onChange,
  readonlyValues = {},
  legacy = false,
  errors = {},
  onVoiceRequest,
}) {
  const [activePageIdx, setActivePageIdx] = useState(0);
  const [transcriptionEnabled, setTranscriptionEnabled] = useState(false);

  // Lookup helper — respects legacy label-keyed vs id-keyed readonlyValues
  const getCapturedVal = (item) =>
    legacy
      ? (readonlyValues[item.label] ?? "")
      : (readonlyValues[item.id] ?? "");

  // ── renderField — core type switch ─────────────────────────────────────────
  function renderField(item) {
    switch (item.type) {

      // ── Structural types — no card wrapper, no Form.Group ──────────────────

      case "title":
        return <h5 className="text-primary fw-bold mb-1">{item.label}</h5>;

      case "divider":
        return <hr className="my-2" />;

      case "static_text":
        return <p className="text-muted small mb-0">{item.label}</p>;

      case "spacer":
        return <div style={{ height: item.height || 24 }} aria-hidden="true" />;

      // ── text ───────────────────────────────────────────────────────────────

      case "text":
        if (mode === "preview") {
          return (
            <Form.Group>
              <span className="badge bg-secondary float-end small">Preview</span>
              <Form.Label className="text-primary fw-bold small mb-1">
                {item.label}
                {item.required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
              <p className={item.defaultValue ? "" : "text-muted"}>
                {item.defaultValue || "No default value"}
              </p>
            </Form.Group>
          );
        }
        if (mode === "readonly") {
          return (
            <Form.Group>
              <Form.Label className="text-primary fw-bold small mb-1">
                {item.label}
                {item.required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
              <Form.Control
                type="text"
                value={getCapturedVal(item) || ""}
                readOnly
              />
            </Form.Group>
          );
        }
        // fill
        return (
          <Form.Group>
            <div className="d-flex align-items-center mb-1">
              {onVoiceRequest && transcriptionEnabled && (
                <button type="button" className="me-2 p-0 border-0 bg-transparent text-primary"
                  onClick={() => onVoiceRequest(item.id)} title="Voice to text">
                  <i className="fa-light fa-microphone" />
                </button>
              )}
              <Form.Label className="text-primary fw-bold small mb-0">
                {item.label}
                {item.required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
            </div>
            <Form.Control
              type="text"
              value={values[item.id] ?? item.defaultValue ?? ""}
              placeholder={item.placeholder || ""}
              onChange={(e) => onChange(item.id, e.target.value)}
              isInvalid={!!errors[item.id]}
            />
            {errors[item.id] && (
              <div className="text-danger small mt-1">{errors[item.id]}</div>
            )}
          </Form.Group>
        );

      // ── memo ───────────────────────────────────────────────────────────────

      case "memo":
        if (mode === "preview") {
          return (
            <Form.Group>
              <span className="badge bg-secondary float-end small">Preview</span>
              <Form.Label className="text-primary fw-bold small mb-1">
                {item.label}
                {item.required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
              <p className={item.defaultValue ? "" : "text-muted"}>
                {item.defaultValue || "No default value"}
              </p>
            </Form.Group>
          );
        }
        if (mode === "readonly") {
          return (
            <Form.Group>
              <Form.Label className="text-primary fw-bold small mb-1">
                {item.label}
                {item.required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                style={{ minHeight: "calc(8 * 1.5rem + 0.75rem)", resize: "none" }}
                value={getCapturedVal(item) || ""}
                readOnly
              />
            </Form.Group>
          );
        }
        // fill
        return (
          <Form.Group>
            <div className="d-flex align-items-center mb-1">
              {onVoiceRequest && transcriptionEnabled && (
                <button type="button" className="me-2 p-0 border-0 bg-transparent text-primary"
                  onClick={() => onVoiceRequest(item.id)} title="Voice to text">
                  <i className="fa-light fa-microphone" />
                </button>
              )}
              <Form.Label className="text-primary fw-bold small mb-0">
                {item.label}
                {item.required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
            </div>
            <Form.Control
              as="textarea"
              rows={8}
              style={{ minHeight: "calc(8 * 1.5rem + 0.75rem)", resize: "vertical" }}
              value={values[item.id] ?? item.defaultValue ?? ""}
              placeholder={item.placeholder || ""}
              onChange={(e) => onChange(item.id, e.target.value)}
              isInvalid={!!errors[item.id]}
            />
            {errors[item.id] && (
              <div className="text-danger small mt-1">{errors[item.id]}</div>
            )}
          </Form.Group>
        );

      // ── checkbox ───────────────────────────────────────────────────────────

      case "checkbox":
        if (mode === "preview") {
          return (
            <Form.Group>
              <span className="badge bg-secondary float-end small">Preview</span>
              <Form.Label className="text-primary fw-bold small mb-1">
                {item.label}
                {item.required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
              {Array.isArray(item.options) && item.options.length > 0
                ? item.options.map((opt, oi) => (
                    <Form.Check
                      key={oi}
                      type="checkbox"
                      label={opt}
                      disabled
                      defaultChecked={
                        Array.isArray(item.defaultValue) &&
                        item.defaultValue.includes(opt)
                      }
                    />
                  ))
                : <p className="text-muted small mb-0">No options defined</p>
              }
            </Form.Group>
          );
        }
        if (mode === "readonly") {
          const savedVals = Array.isArray(getCapturedVal(item)) ? getCapturedVal(item) : [];
          return (
            <Form.Group>
              <Form.Label className="text-primary fw-bold small mb-1">
                {item.label}
                {item.required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
              {(item.options || []).map((opt, oi) => (
                <Form.Check
                  key={oi}
                  type="checkbox"
                  label={opt}
                  checked={savedVals.includes(opt)}
                  disabled
                  readOnly
                />
              ))}
            </Form.Group>
          );
        }
        // fill
        return (
          <Form.Group>
            <Form.Label className="text-primary fw-bold small mb-1">
              {item.label}
              {item.required && <span className="text-danger ms-1">*</span>}
            </Form.Label>
            {(item.options || []).map((opt, oi) => (
              <Form.Check
                key={oi}
                type="checkbox"
                label={opt}
                checked={(values[item.id] || []).includes(opt)}
                onChange={() => {
                  const cur = Array.isArray(values[item.id]) ? values[item.id] : [];
                  onChange(
                    item.id,
                    cur.includes(opt)
                      ? cur.filter((v) => v !== opt)
                      : [...cur, opt]
                  );
                }}
              />
            ))}
            {errors[item.id] && (
              <div className="text-danger small mt-1">{errors[item.id]}</div>
            )}
          </Form.Group>
        );

      // ── radio ──────────────────────────────────────────────────────────────

      case "radio":
        if (mode === "preview") {
          return (
            <Form.Group>
              <span className="badge bg-secondary float-end small">Preview</span>
              <Form.Label className="text-primary fw-bold small mb-1">
                {item.label}
                {item.required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
              {(item.options || []).map((opt, oi) => (
                <Form.Check
                  key={oi}
                  type="radio"
                  label={opt}
                  disabled
                  defaultChecked={item.defaultValue === opt}
                />
              ))}
            </Form.Group>
          );
        }
        if (mode === "readonly") {
          return (
            <Form.Group>
              <Form.Label className="text-primary fw-bold small mb-1">
                {item.label}
                {item.required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
              {(item.options || []).map((opt, oi) => (
                <Form.Check
                  key={oi}
                  type="radio"
                  name={`${item.id}_ro`}
                  label={opt}
                  checked={getCapturedVal(item) === opt}
                  disabled
                  readOnly
                />
              ))}
            </Form.Group>
          );
        }
        // fill
        return (
          <Form.Group>
            <Form.Label className="text-primary fw-bold small mb-1">
              {item.label}
              {item.required && <span className="text-danger ms-1">*</span>}
            </Form.Label>
            {(item.options || []).map((opt, oi) => (
              <Form.Check
                key={oi}
                type="radio"
                name={item.id}
                label={opt}
                checked={values[item.id] === opt}
                onChange={() => onChange(item.id, opt)}
              />
            ))}
            {errors[item.id] && (
              <div className="text-danger small mt-1">{errors[item.id]}</div>
            )}
          </Form.Group>
        );

      // ── yes_no ─────────────────────────────────────────────────────────────

      case "yes_no":
        if (mode === "preview") {
          return (
            <Form.Group>
              <span className="badge bg-secondary float-end small">Preview</span>
              <Form.Label className="text-primary fw-bold small mb-1">
                {item.label}
                {item.required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
              <div>
                {["Yes", "No"].map((opt) => (
                  <Form.Check
                    key={opt}
                    type="radio"
                    name={`${item.id}_preview`}
                    label={opt}
                    inline
                    disabled
                    defaultChecked={item.defaultValue === opt}
                  />
                ))}
              </div>
            </Form.Group>
          );
        }
        if (mode === "readonly") {
          return (
            <Form.Group>
              <Form.Label className="text-primary fw-bold small mb-1">
                {item.label}
                {item.required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
              <div>
                {["Yes", "No"].map((opt) => (
                  <Form.Check
                    key={opt}
                    type="radio"
                    name={`${item.id}_ro`}
                    label={opt}
                    inline
                    checked={getCapturedVal(item) === opt}
                    disabled
                    readOnly
                  />
                ))}
              </div>
            </Form.Group>
          );
        }
        // fill
        return (
          <Form.Group>
            <Form.Label className="text-primary fw-bold small mb-1">
              {item.label}
              {item.required && <span className="text-danger ms-1">*</span>}
            </Form.Label>
            <div>
              {["Yes", "No"].map((opt) => (
                <Form.Check
                  key={opt}
                  type="radio"
                  name={item.id}
                  inline
                  label={opt}
                  checked={values[item.id] === opt}
                  onChange={() => onChange(item.id, opt)}
                />
              ))}
            </div>
            {errors[item.id] && (
              <div className="text-danger small mt-1">{errors[item.id]}</div>
            )}
          </Form.Group>
        );

      // ── numeric ────────────────────────────────────────────────────────────

      case "numeric":
        if (mode === "preview") {
          return (
            <Form.Group>
              <span className="badge bg-secondary float-end small">Preview</span>
              <Form.Label className="text-primary fw-bold small mb-1">
                {item.label}
                {item.required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
              <p className="text-muted">
                {item.defaultValue !== undefined
                  ? String(item.defaultValue) + (item.unit ? " " + item.unit : "")
                  : "No default value"}
              </p>
            </Form.Group>
          );
        }
        if (mode === "readonly") {
          return (
            <Form.Group>
              <Form.Label className="text-primary fw-bold small mb-1">
                {item.label}
                {item.required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
              <div className="d-flex align-items-center gap-2">
                <Form.Control
                  type="number"
                  style={{ maxWidth: 120 }}
                  value={getCapturedVal(item) ?? ""}
                  readOnly
                />
                {item.unit && <span className="text-muted small">{item.unit}</span>}
              </div>
            </Form.Group>
          );
        }
        // fill
        return (
          <Form.Group>
            <Form.Label className="text-primary fw-bold small mb-1">
              {item.label}
              {item.required && <span className="text-danger ms-1">*</span>}
            </Form.Label>
            <div className="d-flex align-items-center gap-2">
              <Form.Control
                type="number"
                style={{ maxWidth: 120 }}
                value={values[item.id] ?? ""}
                placeholder={item.placeholder || ""}
                onChange={(e) => onChange(item.id, e.target.value)}
                isInvalid={!!errors[item.id]}
              />
              {item.unit && <span className="text-muted small">{item.unit}</span>}
            </div>
            {errors[item.id] && (
              <div className="text-danger small mt-1">{errors[item.id]}</div>
            )}
          </Form.Group>
        );

      // ── pain_scale ─────────────────────────────────────────────────────────

      case "pain_scale":
        if (mode === "preview") {
          return (
            <Form.Group>
              <span className="badge bg-secondary float-end small">Preview</span>
              <Form.Label className="text-primary fw-bold small mb-1">
                {item.label}
                {item.required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
              <div className="d-flex justify-content-between text-muted small mb-1">
                <span>0 — No pain</span>
                <span>10 — Worst pain</span>
              </div>
              <Form.Range min={0} max={10} step={1} value={item.defaultValue ?? 5} disabled />
              <div className="text-center fw-bold">{item.defaultValue ?? 5}</div>
            </Form.Group>
          );
        }
        if (mode === "readonly") {
          const rv = getCapturedVal(item);
          const rvNum = rv !== "" && rv !== undefined ? Number(rv) : 5;
          return (
            <Form.Group>
              <Form.Label className="text-primary fw-bold small mb-1">
                {item.label}
                {item.required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
              <div>
                <div className="d-flex justify-content-between text-muted small mb-1">
                  <span>0 — No pain</span>
                  <span>10 — Worst pain</span>
                </div>
                <Form.Range min={0} max={10} step={1} value={rvNum} disabled style={{ pointerEvents: "none", opacity: 1 }} />
                <div className="text-center fw-bold">{rv !== "" && rv !== undefined ? String(rv) + "/10" : "—"}</div>
              </div>
            </Form.Group>
          );
        }
        // fill
        return (
          <Form.Group>
            <Form.Label className="text-primary fw-bold small mb-1">
              {item.label}
              {item.required && <span className="text-danger ms-1">*</span>}
            </Form.Label>
            <div>
              <div className="d-flex justify-content-between text-muted small mb-1">
                <span>0 — No pain</span>
                <span>10 — Worst pain</span>
              </div>
              <Form.Range
                min={0}
                max={10}
                step={1}
                value={values[item.id] ?? item.defaultValue ?? 5}
                onChange={(e) => onChange(item.id, Number(e.target.value))}
              />
              <div className="text-center fw-bold">{values[item.id] ?? "—"}</div>
            </div>
          </Form.Group>
        );

      // ── dropdown ───────────────────────────────────────────────────────────

      case "dropdown":
        if (mode === "preview") {
          return (
            <Form.Group>
              <span className="badge bg-secondary float-end small">Preview</span>
              <Form.Label className="text-primary fw-bold small mb-1">
                {item.label}
                {item.required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
              <p className="text-muted">{item.defaultValue || "Select…"}</p>
            </Form.Group>
          );
        }
        if (mode === "readonly") {
          return (
            <Form.Group>
              <Form.Label className="text-primary fw-bold small mb-1">
                {item.label}
                {item.required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
              <Form.Select value={getCapturedVal(item) ?? ""} disabled>
                <option value="">Select…</option>
                {(item.options || []).map((opt, i) => (
                  <option key={i} value={opt}>{opt}</option>
                ))}
              </Form.Select>
            </Form.Group>
          );
        }
        // fill
        return (
          <Form.Group>
            <Form.Label className="text-primary fw-bold small mb-1">
              {item.label}
              {item.required && <span className="text-danger ms-1">*</span>}
            </Form.Label>
            <Form.Select
              value={values[item.id] ?? ""}
              onChange={(e) => onChange(item.id, e.target.value)}
              isInvalid={!!errors[item.id]}
            >
              <option value="">Select…</option>
              {(item.options || []).map((opt, i) => (
                <option key={i} value={opt}>{opt}</option>
              ))}
            </Form.Select>
            {errors[item.id] && (
              <div className="text-danger small mt-1">{errors[item.id]}</div>
            )}
          </Form.Group>
        );

      // ── date ───────────────────────────────────────────────────────────────

      case "date":
        if (mode === "preview") {
          return (
            <Form.Group>
              <span className="badge bg-secondary float-end small">Preview</span>
              <Form.Label className="text-primary fw-bold small mb-1">
                {item.label}
                {item.required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
              <p className="text-muted">{item.defaultValue || "No default date"}</p>
            </Form.Group>
          );
        }
        if (mode === "readonly") {
          return (
            <Form.Group>
              <Form.Label className="text-primary fw-bold small mb-1">
                {item.label}
                {item.required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
              <Form.Control
                type="date"
                value={getCapturedVal(item) ?? ""}
                readOnly
              />
            </Form.Group>
          );
        }
        // fill
        return (
          <Form.Group>
            <Form.Label className="text-primary fw-bold small mb-1">
              {item.label}
              {item.required && <span className="text-danger ms-1">*</span>}
            </Form.Label>
            <Form.Control
              type="date"
              value={values[item.id] ?? ""}
              onChange={(e) => onChange(item.id, e.target.value)}
              isInvalid={!!errors[item.id]}
            />
            {errors[item.id] && (
              <div className="text-danger small mt-1">{errors[item.id]}</div>
            )}
          </Form.Group>
        );

      // ── image ──────────────────────────────────────────────────────────────

      case "image":
        if (mode === "fill") {
          return (
            <Form.Group>
              <Form.Label className="text-primary fw-bold small mb-1">
                {item.label}
                {item.required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
              <ImageAnnotationField
                item={item}
                value={values[item.id] ?? null}
                onChange={onChange}
              />
            </Form.Group>
          );
        }
        if (mode === "readonly") {
          return (
            <Form.Group>
              <Form.Label className="text-primary fw-bold small mb-1">
                {item.label}
                {item.required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
              <ImageReadonlyField
                item={item}
                savedPaths={getCapturedVal(item) || null}
              />
            </Form.Group>
          );
        }
        // preview — plain img, no annotation controls
        return (
          <Form.Group>
            <span className="badge bg-secondary float-end small">Preview</span>
            <Form.Label className="text-primary fw-bold small mb-1">
              {item.label}
              {item.required && <span className="text-danger ms-1">*</span>}
            </Form.Label>
            {item.imageUrl
              ? (
                <img
                  src={item.imageUrl}
                  alt={item.label || "Template image"}
                  className="rounded d-block mx-auto"
                  style={{ width: (item.imageWidth || 100) + "%", maxWidth: "100%" }}
                />
              )
              : <p className="text-muted small">No image set</p>
            }
          </Form.Group>
        );

      default:
        return (
          <p className="text-muted small">Unknown field type: {item.type}</p>
        );
    }
  }

  // ── renderItem — dispatch with card wrapper logic ──────────────────────────
  function renderItem(item) {
    if (item.kind === "group") {
      return (
        <Col md={item.colSpan || 12} key={item.id}>
          <div
            className="border rounded p-3 mb-2"
            style={{ background: mode === "preview" ? "#f8f9fa" : "#CFE3FF14" }}
          >
            {item.groupTitle && (
              <h6 className="text-secondary fw-bold mb-2">{item.groupTitle}</h6>
            )}
            <Row className="g-2">
              {(item.children || []).map((child) => (
                <Col md={child.colSpan || 12} key={child.id}>
                  {renderField(child)}
                </Col>
              ))}
            </Row>
          </div>
        </Col>
      );
    }

    const noCard = ["title", "divider", "static_text", "spacer"].includes(item.type);
    const colSpan = item.type === "divider" ? 12 : (item.colSpan || 12);

    return (
      <Col md={colSpan} key={item.id}>
        {noCard
          ? renderField(item)
          : <div className="border shadow-sm rounded p-3">{renderField(item)}</div>
        }
      </Col>
    );
  }

  // ── Render — page tabs + content ───────────────────────────────────────────
  const safePage = (pages || [])[activePageIdx] || (pages || [])[0];

  return (
    <div>
      {mode === "fill" && onVoiceRequest && (
        <div className="d-flex align-items-center mb-2">
          <Form.Check
            type="switch"
            id="transcription-toggle"
            label="Transcription"
            checked={transcriptionEnabled}
            onChange={(e) => setTranscriptionEnabled(e.target.checked)}
            className="small"
          />
        </div>
      )}
      {(pages || []).length > 1 && (
        <div className="d-flex flex-wrap gap-2 mb-2">
          {pages.map((p, idx) => (
            <button
              key={p.pageId}
              className={"btn btn-sm " + (idx === activePageIdx ? "btn-primary" : "btn-outline-secondary")}
              onClick={() => setActivePageIdx(idx)}
            >
              {p.pageTitle}
            </button>
          ))}
        </div>
      )}
      <Row className="g-2">
        {safePage?.items?.length > 0
          ? safePage.items.map((item) => renderItem(item))
          : <Col><p className="text-muted">No elements on this page.</p></Col>
        }
      </Row>
    </div>
  );
}
